import { Box, Divider, List, ListItem, ListSubheader, Popover, Stack, Typography } from "@mui/material";
import PopoverMenuItem from "@entrilia/shared/components/PopoverMenuItem";
import { useCallback, useMemo, useRef, useState } from "react";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import SearchField from "@entrilia/shared/components/inputs/SearchField";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis.tsx";
import type { ReportGroup } from "../../../../../../../api/types/svTypes.ts";
import type { SharedItemType } from "../../viewer/common/sharing/ShareReport.types";
import type { ReportSelectorItem } from "./reportSelectorHelpers";
import {
  getFilteredGroupedReports,
  getGroupedListItemNodes,
  getNonGroupedListItemNodes,
  handleArrowDownMenuNavigation,
  handleArrowUpMenuNavigation,
  handleEnterMenuNavigation,
  matchesReportFilter,
} from "./reportSelectorHelpers";
import {
  clampSize,
  DEFAULT_HEIGHT,
  DEFAULT_WIDTH,
  MAX_HEIGHT,
  MAX_WIDTH,
  MIN_HEIGHT,
  MIN_WIDTH,
  reportSelectorSizeStorage,
} from "./reportSelectorSizeStorage";
import type { Size } from "./ResizableCornerWrapper";
import ResizableCornerWrapper from "./ResizableCornerWrapper";

interface Props {
  anchorEl: HTMLElement | null;
  clientCode: string;
  currentReportName: string | undefined;
  groups: Record<string, ReportGroup[]> | undefined;
  reports: ReportSelectorItem[];
  isLoading: boolean;
  itemType?: SharedItemType;
  onClose: () => void;
  onSelectNewReport: (reportId: string) => void;
}

const ReportSelectorMenuWithGroupedList = ({
  anchorEl,
  onClose,
  reports,
  clientCode,
  currentReportName,
  onSelectNewReport,
  groups,
  isLoading,
  itemType,
}: Props) => {
  const [textFilter, setTextFilter] = useState("");
  const clientGroups = groups?.[clientCode];
  const isGrouped = !!clientGroups && clientGroups.length > 0;

  const filteredGroupedReports = useMemo(() => {
    if (!isGrouped) return [];
    return getFilteredGroupedReports(reports, clientGroups, textFilter) || [];
  }, [reports, clientGroups, textFilter, isGrouped]);

  const filteredFlatReports = useMemo(() => {
    if (isGrouped) return [];
    return !textFilter
      ? reports
      : reports.filter((f) => matchesReportFilter(f, textFilter)).sort((a, b) => a.name.localeCompare(b.name));
  }, [reports, textFilter, isGrouped]);

  const hasResults = isGrouped ? filteredGroupedReports.length > 0 : filteredFlatReports.length > 0;

  const listRef = useRef<HTMLUListElement>(null);
  const [selectedReportName, setSelectedReportName] = useState<string | undefined>(currentReportName);
  const isInitiallyScrolledRef = useRef<boolean>(false);

  const [size, setSize] = useState<Size>(() => {
    const stored = reportSelectorSizeStorage.get();
    return clampSize(stored ?? { widthPx: DEFAULT_WIDTH, heightPx: DEFAULT_HEIGHT });
  });

  const handleSizeCommit = useCallback((next: Size) => reportSelectorSizeStorage.save(next), []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (!listRef.current) {
      return;
    }
    const listItemNodes = isGrouped
      ? getGroupedListItemNodes(listRef.current)
      : getNonGroupedListItemNodes(listRef.current);
    if (event.key === "ArrowDown") {
      event.preventDefault();
      handleArrowDownMenuNavigation(listItemNodes, selectedReportName, setSelectedReportName);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      handleArrowUpMenuNavigation(listItemNodes, selectedReportName, setSelectedReportName);
    } else if (event.key === "Enter") {
      event.preventDefault();
      handleEnterMenuNavigation(listItemNodes, selectedReportName, onSelectNewReport);
    }
  };

  return (
    <Popover
      disableRestoreFocus // Without this prop, the TextField inside the SearchField will lose its autofocus. https://github.com/mui/material-ui/issues/33004#issuecomment-1455260156
      open={!!anchorEl}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      slotProps={{
        paper: {
          sx: {
            boxShadow: "0px 3px 4px rgba(0, 0, 0, 0.14), 0px 1px 8px rgba(0, 0, 0, 0.12)",
            width: `${size.widthPx}px`,
            height: `${size.heightPx}px`,
            maxWidth: "none",
            maxHeight: "none",
          },
        },
      }}
      onKeyDown={handleKeyDown}
    >
      <ResizableCornerWrapper
        size={size}
        minWidth={MIN_WIDTH}
        maxWidth={MAX_WIDTH}
        minHeight={MIN_HEIGHT}
        maxHeight={MAX_HEIGHT}
        onSizeChange={setSize}
        onCommit={handleSizeCommit}
      >
        <Stack sx={{ flex: 1, height: "100%", width: "100%" }}>
          <Box sx={{ flexDirection: "row", justifyContent: "stretch", display: "flex", p: 2 }}>
            <SearchField
              onSearch={(t) => setTextFilter(t.trim().toLowerCase())}
              autoFocus
              debounceTimeMs={300}
              fullWidth
            />
          </Box>
          <Divider />
          <ScrollableFlexContainer>
            {isLoading && <InlineLoader overlay />}
            {!isLoading && (
              <List
                disablePadding
                sx={{ width: "100%" }}
                ref={listRef}
                {...(isGrouped ? {} : { tabIndex: 0, role: "listbox", "aria-label": "Reports list" })}
              >
                {!hasResults && (
                  <ListItem sx={{ pr: 4, justifyContent: "center" }}>
                    <Typography>No {itemType ?? "report"}s found</Typography>
                  </ListItem>
                )}
                {hasResults &&
                  isGrouped &&
                  filteredGroupedReports.map(([group, groupReports]) => (
                    <ListItem key={`section-${group}`} sx={{ py: 0 }}>
                      <List disablePadding sx={{ width: "100%" }}>
                        <ListSubheader
                          disableSticky
                          sx={{ py: "0.3rem", px: 0, lineHeight: "inherit" }}
                          aria-hidden="true"
                        >
                          <Typography variant="caption">{group}</Typography>
                        </ListSubheader>
                        {groupReports.map((r) => (
                          <PopoverMenuItem
                            key={`item-${group}-${r.reportId}`}
                            sx={{ py: 1, justifyContent: "space-between", gap: 3 }}
                            selected={selectedReportName === r.name}
                            onClick={() => onSelectNewReport(r.reportId)}
                            ref={(el) => {
                              if (currentReportName === r.name && !isInitiallyScrolledRef.current) {
                                isInitiallyScrolledRef.current = true;
                                el?.scrollIntoView({ behavior: "instant", block: "center" });
                              }
                            }}
                            role="option"
                            aria-label={r.name}
                            data-report-id={r.reportId}
                            tabIndex={-1}
                          >
                            <TypographyTooltipEllipsis text={r.name} tooltipPlacement="top" observeContainerResize />
                            {r.reportType && <Typography color="textSecondary">{r.reportType}</Typography>}
                          </PopoverMenuItem>
                        ))}
                      </List>
                    </ListItem>
                  ))}
                {hasResults &&
                  !isGrouped &&
                  filteredFlatReports.map((r) => (
                    <PopoverMenuItem
                      key={`item-${r.reportId}`}
                      sx={{ py: 1, justifyContent: "space-between", gap: 3 }}
                      selected={selectedReportName === r.name}
                      onClick={() => onSelectNewReport(r.reportId)}
                      ref={(el) => {
                        if (currentReportName === r.name && !isInitiallyScrolledRef.current) {
                          isInitiallyScrolledRef.current = true;
                          el?.scrollIntoView({ behavior: "instant", block: "center" });
                        }
                      }}
                      role="option"
                      aria-label={r.name}
                      data-report-id={r.reportId}
                      tabIndex={-1}
                    >
                      <TypographyTooltipEllipsis text={r.name} tooltipPlacement="top" observeContainerResize />
                      {r.reportType && <Typography color="textSecondary">{r.reportType}</Typography>}
                    </PopoverMenuItem>
                  ))}
              </List>
            )}
          </ScrollableFlexContainer>
        </Stack>
      </ResizableCornerWrapper>
    </Popover>
  );
};

export default ReportSelectorMenuWithGroupedList;
